import {
  KEYBOARD_LAYOUT_ROWS,
  type KeyboardLayout,
  REDUCED_MOTION_PREFERENCES,
  type ReducedMotionPreference,
} from '@words-happen/ui-components';
import { type AccountSettings, DEFAULT_ACCOUNT_SETTINGS } from '../api/settings';

/** The `localStorage` key holding the last account settings loaded on this device. */
export const ACCOUNT_SETTINGS_CACHE_KEY = 'words-happen:account-settings';

/**
 * The signed-in player's account settings as last loaded, kept on the device so the first paint can use them before
 * the API answers. Cleared on sign-out.
 */
export interface AccountSettingsCache {
  /** The cached settings, or undefined when there are none or they can't be read. */
  read: () => AccountSettings | undefined;
  write: (settings: AccountSettings) => void;
  clear: () => void;
}

const isReducedMotion = (value: unknown): value is ReducedMotionPreference =>
  REDUCED_MOTION_PREFERENCES.includes(value as ReducedMotionPreference);

const isKeyboardLayout = (value: unknown): value is KeyboardLayout =>
  Object.hasOwn(KEYBOARD_LAYOUT_ROWS, String(value));

/**
 * The cached settings. Without a valid Reduced motion there is no cache; a missing or unknown keyboard layout (a cache
 * written before the setting existed) reads as the default.
 */
const parse = (raw: string | null): AccountSettings | undefined => {
  if (!raw) {
    return undefined;
  }
  const value = JSON.parse(raw) as Partial<Record<keyof AccountSettings, unknown>> | null;
  if (!isReducedMotion(value?.reducedMotion)) {
    return undefined;
  }
  return {
    reducedMotion: value.reducedMotion,
    keyboardLayout: isKeyboardLayout(value.keyboardLayout)
      ? value.keyboardLayout
      : DEFAULT_ACCOUNT_SETTINGS.keyboardLayout,
  };
};

/**
 * The cache in `localStorage`. Every access is wrapped in try/catch: when storage is unavailable (private browsing,
 * blocked site data) or holds something unreadable, there is simply no cache.
 */
export const storageAccountSettingsCache = (
  storage: () => Storage | undefined = () => globalThis.localStorage,
): AccountSettingsCache => ({
  read: () => {
    try {
      return parse(storage()?.getItem(ACCOUNT_SETTINGS_CACHE_KEY) ?? null);
    } catch {
      return undefined;
    }
  },
  write: (settings) => {
    try {
      storage()?.setItem(ACCOUNT_SETTINGS_CACHE_KEY, JSON.stringify(settings));
    } catch {
      // No cache: the next visit paints from the defaults until the API answers.
    }
  },
  clear: () => {
    try {
      storage()?.removeItem(ACCOUNT_SETTINGS_CACHE_KEY);
    } catch {
      // Nothing was stored.
    }
  },
});

/** An in-memory cache, for showcases and tests. */
export const memoryAccountSettingsCache = (initial?: AccountSettings): AccountSettingsCache => {
  let settings = initial;
  return {
    read: () => settings,
    write: (next) => {
      settings = next;
    },
    clear: () => {
      settings = undefined;
    },
  };
};
